import useConfig from 'modules/config/state/use-config'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import orderContactAnswersSlice from './contact-answers-slice'
import { OrderContactAnswersSlice } from './contact-answers-types'
import {
  makeOrderContactAnswersPayload,
  makeOrderContactAnswersValidationSchema,
} from './contact-answers-utils'
import { formatAnswerDate } from 'utils/functions/dateUtils'
import { updateTransactionAnswers } from 'redux/services/transaction'
import { makeOrderCustomAnswersPayload } from '../custom-answers/custom-answers-utils'
import { OrderCustomAnswersSlice } from '../custom-answers/custom-answers-types'

export type ContactAnswersSavePayload = {
  contactAnswers?: OrderContactAnswersSlice
  customAnswers?: OrderCustomAnswersSlice
}

const useOrderContactAnswers = () => {
  const config = useConfig()
  const dispatch = useDispatch<TypedDispatch<RootState>>()
  const value = useSelector((state: RootState) => state.order.contactAnswers)
  const customAnswersValue = useSelector((state: RootState) => state.order.customAnswers)
  const schema = useMemo(() => makeOrderContactAnswersValidationSchema(config), [config])

  const { confirmationId, confirmationToken } = useSelector(
    (state: RootState) => state.order.transaction,
  )

  const setValue = useCallback(
    (value: OrderContactAnswersSlice) => {
      const formattedValue: OrderContactAnswersSlice = value.birthdate
        ? { ...value, birthdate: formatAnswerDate(value.birthdate) }
        : value

      dispatch(orderContactAnswersSlice.actions.setValue(formattedValue))
    },
    [dispatch],
  )

  const save = useCallback(
    ({ contactAnswers = value, customAnswers = customAnswersValue }: ContactAnswersSavePayload) => {
      if (!confirmationToken) return

      dispatch(
        updateTransactionAnswers.initiate({
          transactionId: String(confirmationId),
          transactionAnswers: {
            /**
             * PS. need to send both custom and contact questions because answers much contain
             * all the answers when we update the transaction.
             */
            answers: [
              ...makeOrderContactAnswersPayload(config, contactAnswers),
              ...makeOrderCustomAnswersPayload(config, customAnswers),
            ],
          },
          transactionToken: confirmationToken,
        }),
      )
    },
    [value, customAnswersValue, confirmationToken, dispatch, confirmationId, config],
  )

  return useMemo(
    () => ({
      value,
      schema,
      setValue,
      save,
    }),
    [value, schema, setValue, save],
  )
}

export default useOrderContactAnswers
